import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { OperationsDashboardSkeleton } from "@/components/SkeletonLoaders";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle, TrendingUp, TrendingDown, Shield, ClipboardCheck,
  Building2, DollarSign, PieChart, Activity, Target, Check, Pencil,
  BarChart3, Hotel, Calculator, Clock, Utensils, FileText, Trophy,
  LayoutDashboard, Bell, Zap, ChevronRight, ArrowUpRight, ArrowDownRight, Users,
} from "lucide-react";
import { differenceInDays, parseISO, format, startOfWeek, endOfWeek } from "date-fns";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

import PermitRenewalTracker from "./PermitRenewalTracker";
import MonthlyCheckIn from "./MonthlyCheckIn";
import HealthInspectionPrep from "./HealthInspectionPrep";
import LeaseTracker from "./LeaseTracker";
import {
  RevPARTracker, GOPCalculator, LaborProductivityIndex,
  FBCostVarianceReport, DepartmentPLBreakdown, ManagerDailyReport,
  KPIBenchmarkingTool,
} from "./AdvancedOperationsModules";

const MIAMI_AVG = { foodCostPct: 30, laborCostPct: 32, rentPct: 8 };
const VIOLATIONS_COUNT = 20;

type NavSection =
  | "overview"
  | "revenue"
  | "fb"
  | "labor"
  | "inventory"
  | "staff-rota"
  | "permits"
  | "inspection"
  | "lease"
  | "benchmarks"
  | "daily-report"
  | "checkin";

const NAV_ITEMS: { id: NavSection; label: string; icon: React.ElementType; group: string }[] = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, group: "General" },
  { id: "revenue", label: "Revenue & P&L", icon: DollarSign, group: "Financial" },
  { id: "fb", label: "F&B Performance", icon: Utensils, group: "Financial" },
  { id: "labor", label: "Labor", icon: Users, group: "Financial" },
  { id: "inventory", label: "Inventory", icon: Calculator, group: "Operations" },
  { id: "staff-rota", label: "Staff Rota", icon: Clock, group: "Operations" },
  { id: "permits", label: "Permits", icon: Shield, group: "Compliance" },
  { id: "inspection", label: "Inspections", icon: ClipboardCheck, group: "Compliance" },
  { id: "lease", label: "Lease", icon: Building2, group: "Compliance" },
  { id: "benchmarks", label: "Benchmarks", icon: Trophy, group: "Analytics" },
  { id: "daily-report", label: "Daily Report", icon: FileText, group: "Analytics" },
  { id: "checkin", label: "Monthly Check-In", icon: Activity, group: "Analytics" },
];

// ─── Mini Health Gauge ──────────────────────────────────────────────
function MiniGauge({ score }: { score: number }) {
  const color = score >= 70 ? "text-primary" : score >= 40 ? "text-accent" : "text-destructive";
  const bgColor = score >= 70 ? "bg-primary" : score >= 40 ? "bg-accent" : "bg-destructive";
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-14 h-14">
        <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
          <circle cx="20" cy="20" r="16" fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
          <circle
            cx="20" cy="20" r="16" fill="none"
            stroke={score >= 70 ? "hsl(var(--primary))" : score >= 40 ? "hsl(var(--accent))" : "hsl(var(--destructive))"}
            strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${(score / 100) * 100.53} 100.53`}
            className="transition-all duration-700"
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-sm font-medium ${color}`}>
          {score}
        </span>
      </div>
      <div>
        <div className={`text-xs font-medium ${color}`}>
          {score >= 70 ? "Healthy" : score >= 40 ? "Attention" : "Critical"}
        </div>
        <div className="text-[10px] text-muted-foreground">Health Score</div>
      </div>
    </div>
  );
}

// ─── Inventory Launcher ──────────────────────────────────────────────
function InventoryLauncher() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [count, setCount] = useState<number | null>(null);
  const [lowCount, setLowCount] = useState<number>(0);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("inventory_items")
        .select("par_level, current_stock")
        .eq("user_id", user.id);
      const items = data ?? [];
      setCount(items.length);
      setLowCount(items.filter((i) => Number(i.par_level) > 0 && Number(i.current_stock) < Number(i.par_level)).length);
    })();
  }, [user]);

  return (
    <Card className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 className="font-heading text-lg font-medium text-foreground">Open Purchasing & Stocktake</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {count === null
            ? "Build your item catalog, count stock, and generate purchase orders automatically."
            : count === 0
            ? "No items yet — start your catalog to track par levels and auto-generate POs."
            : `${count} item${count === 1 ? "" : "s"} in your catalog · ${lowCount} below par`}
        </p>
      </div>
      <Button onClick={() => navigate("/inventory")} className="gap-2 shrink-0">
        Open module <ChevronRight className="w-4 h-4" />
      </Button>
    </Card>
  );
}

// ─── Inventory Tracker (legacy, kept for fallback only) ──────────────
function InventoryTracker() {
  const [items, setItems] = useState([
    { name: "Chicken Breast", unit: "lbs", onHand: 45, parLevel: 60 },
    { name: "Olive Oil", unit: "bottles", onHand: 8, parLevel: 12 },
    { name: "Flour (AP)", unit: "lbs", onHand: 15, parLevel: 50 },
    { name: "Napkins", unit: "packs", onHand: 30, parLevel: 20 },
    { name: "Tomatoes", unit: "lbs", onHand: 5, parLevel: 25 },
    { name: "Coffee Beans", unit: "lbs", onHand: 12, parLevel: 15 },
  ]);

  const lowStock = items.filter(i => i.onHand < i.parLevel * 0.5);
  const warning = items.filter(i => i.onHand >= i.parLevel * 0.5 && i.onHand < i.parLevel);

  const updateItem = (idx: number, field: "onHand" | "parLevel", val: number) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Calculator className="w-4 h-4 text-primary" />
          Stock Levels
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {lowStock.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <div className="text-xs">
              <span className="font-medium text-destructive">Low stock alert:</span>{" "}
              <span className="text-muted-foreground">{lowStock.map(i => i.name).join(", ")}</span>
            </div>
          </div>
        )}
        <div className="divide-y divide-border">
          {items.map((item, idx) => {
            const pct = item.parLevel > 0 ? (item.onHand / item.parLevel) * 100 : 100;
            const statusColor = pct < 50 ? "text-destructive" : pct < 100 ? "text-accent" : "text-primary";
            return (
              <div key={item.name} className="flex items-center justify-between py-2.5 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${pct < 50 ? "bg-destructive" : pct < 100 ? "bg-accent" : "bg-primary"}`} />
                    <span className="text-sm font-medium text-foreground truncate">{item.name}</span>
                  </div>
                  <div className="ml-3.5 mt-1">
                    <Progress value={Math.min(pct, 100)} className="h-1.5" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs shrink-0">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={item.onHand}
                    onChange={e => updateItem(idx, "onHand", parseInt(e.target.value) || 0)}
                    className="w-14 h-7 text-xs text-center [appearance:textfield]"
                  />
                  <span className="text-muted-foreground">/</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={item.parLevel}
                    onChange={e => updateItem(idx, "parLevel", parseInt(e.target.value) || 0)}
                    className="w-14 h-7 text-xs text-center [appearance:textfield]"
                  />
                  <span className="text-muted-foreground text-[10px] w-10">{item.unit}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Staff Rota Section ──────────────────────────────────────────────
function StaffRotaSection() {
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [staff, setStaff] = useState([
    { name: "Maria G.", role: "Server", rate: 18, hours: [8, 8, 0, 8, 8, 8, 0] },
    { name: "James K.", role: "Line Cook", rate: 22, hours: [8, 8, 8, 8, 8, 0, 0] },
    { name: "Anna P.", role: "Host", rate: 16, hours: [0, 6, 6, 6, 6, 6, 0] },
    { name: "Carlos R.", role: "Dishwasher", rate: 15, hours: [8, 8, 8, 0, 8, 8, 8] },
    { name: "Sarah L.", role: "Bartender", rate: 20, hours: [0, 0, 7, 7, 7, 7, 7] },
  ]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newRate, setNewRate] = useState("15");

  const updateHours = (si: number, di: number, val: string) => {
    const n = parseInt(val) || 0;
    setStaff(prev => prev.map((s, i) => i === si ? { ...s, hours: s.hours.map((h, j) => j === di ? Math.min(Math.max(n, 0), 24) : h) } : s));
  };

  const addMember = () => {
    if (!newName.trim() || !newRole.trim()) return;
    setStaff(prev => [...prev, { name: newName.trim(), role: newRole.trim(), rate: parseFloat(newRate) || 15, hours: [0, 0, 0, 0, 0, 0, 0] }]);
    setNewName(""); setNewRole(""); setNewRate("15"); setShowAdd(false);
  };

  const removeMember = (idx: number) => setStaff(prev => prev.filter((_, i) => i !== idx));

  const totalWeekly = staff.reduce((sum, s) => sum + s.hours.reduce((a, h) => a + h * s.rate, 0), 0);
  const totalMonthly = Math.round(totalWeekly * 4.3);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Weekly Schedule
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">${totalWeekly.toLocaleString()}/wk</Badge>
            <Badge variant="outline" className="text-xs">${totalMonthly.toLocaleString()}/mo</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Staff</th>
                <th className="text-left py-2 pr-2 font-medium text-muted-foreground">Role</th>
                <th className="text-right py-2 pr-2 font-medium text-muted-foreground">$/hr</th>
                {DAYS.map(d => (
                  <th key={d} className="text-center py-2 px-1 font-medium text-muted-foreground w-10">{d}</th>
                ))}
                <th className="text-right py-2 pl-2 font-medium text-muted-foreground">Cost</th>
                <th className="w-6" />
              </tr>
            </thead>
            <tbody>
              {staff.map((s, si) => {
                const weeklyCost = s.hours.reduce((a, h) => a + h * s.rate, 0);
                return (
                  <tr key={si} className="border-b border-border/50">
                    <td className="py-1.5 pr-3 font-medium text-foreground">{s.name}</td>
                    <td className="py-1.5 pr-2 text-muted-foreground">{s.role}</td>
                    <td className="py-1.5 pr-2 text-right text-muted-foreground">${s.rate}</td>
                    {s.hours.map((h, di) => (
                      <td key={di} className="text-center py-1 px-0.5">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={h || ""}
                          onChange={e => updateHours(si, di, e.target.value)}
                          className="w-8 h-6 text-[10px] text-center p-0 font-medium"
                          placeholder="—"
                        />
                      </td>
                    ))}
                    <td className="py-1.5 pl-2 text-right font-medium text-foreground">${weeklyCost}</td>
                    <td className="py-1.5 pl-1">
                      <button onClick={() => removeMember(si)} className="text-muted-foreground hover:text-destructive">
                        <AlertTriangle className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-border font-medium">
                <td colSpan={3} className="py-2 pr-3 text-foreground">Totals</td>
                {DAYS.map((_, di) => (
                  <td key={di} className="text-center py-2 text-muted-foreground text-[10px]">
                    {staff.reduce((sum, s) => sum + s.hours[di], 0)}h
                  </td>
                ))}
                <td className="py-2 pl-2 text-right text-primary">${totalWeekly.toLocaleString()}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>

        {showAdd ? (
          <div className="mt-3 flex items-end gap-2 flex-wrap">
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground">Name</label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} className="h-7 text-xs w-28" placeholder="Name" />
            </div>
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground">Role</label>
              <Input value={newRole} onChange={e => setNewRole(e.target.value)} className="h-7 text-xs w-24" placeholder="Role" />
            </div>
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground">$/hr</label>
              <Input type="text" inputMode="numeric" value={newRate} onChange={e => setNewRate(e.target.value.replace(/[^0-9.]/g, ""))} className="h-7 text-xs w-16" />
            </div>
            <Button size="sm" className="h-7 text-xs" onClick={addMember}>Add</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowAdd(false)}>Cancel</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="mt-3 text-xs h-7" onClick={() => setShowAdd(true)}>
            + Add Staff Member
          </Button>
        )}

        <div className="mt-3 p-2 rounded-lg bg-muted/30 text-xs text-muted-foreground">
          Weekly: <strong className="text-foreground">${totalWeekly.toLocaleString()}</strong> · Monthly (×4.3): <strong className="text-foreground">${totalMonthly.toLocaleString()}</strong>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Overview Section ────────────────────────────────────────────────
function OverviewContent({
  metrics, healthScore, inspPct, permitDays, nextPermit, chartData,
  goalTarget, goalActual, goalPct, weekStart, weekEnd,
  editingGoal, setEditingGoal, goalInput, setGoalInput, actualInput, setActualInput, saveGoal,
}: any) {
  return (
    <div className="space-y-5">
      {/* Health Score + KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: "Cash Flow", icon: DollarSign,
            value: metrics ? `${metrics.cashFlow >= 0 ? "+" : "-"}$${Math.abs(metrics.cashFlow).toLocaleString()}` : "–",
            color: metrics?.cashFlow >= 0 ? "text-primary" : "text-destructive",
            sub: "This month",
          },
          {
            label: "Food Cost", icon: Utensils,
            value: metrics ? `${metrics.foodPct.toFixed(1)}%` : "–",
            color: metrics && metrics.foodPct > 32 ? "text-destructive" : "text-foreground",
            sub: `Avg: ${MIAMI_AVG.foodCostPct}%`,
          },
          {
            label: "Labor Cost", icon: Users,
            value: metrics ? `${metrics.laborPct.toFixed(1)}%` : "–",
            color: metrics && metrics.laborPct > 35 ? "text-destructive" : "text-foreground",
            sub: `Avg: ${MIAMI_AVG.laborCostPct}%`,
          },
          {
            label: "Inspection", icon: ClipboardCheck,
            value: `${inspPct}%`,
            color: inspPct >= 80 ? "text-primary" : inspPct >= 50 ? "text-accent" : "text-destructive",
            sub: "Readiness",
          },
        ].map((kpi, i) => (
          <Card key={i} className="bg-card border-border/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <kpi.icon className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{kpi.label}</span>
              </div>
              <div className={`text-xl font-medium tabular-nums ${kpi.color}`}>{kpi.value}</div>
              <p className="text-[10px] text-muted-foreground mt-0.5">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue Goal */}
      <Card className="bg-card border-border/60">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Weekly Revenue Goal
              </span>
              <span className="text-[10px] text-muted-foreground">({weekStart} – {weekEnd})</span>
            </div>
            {!editingGoal && (
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => {
                setGoalInput(goalTarget.toString());
                setActualInput(goalActual.toString());
                setEditingGoal(true);
              }}>
                <Pencil className="w-3 h-3 mr-1" /> {goalTarget > 0 ? "Edit" : "Set"}
              </Button>
            )}
          </div>
          {editingGoal ? (
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1">
                <label className="text-[10px] text-muted-foreground">Target ($)</label>
                <Input type="number" className="h-8 text-sm" value={goalInput} onChange={e => setGoalInput(e.target.value)} />
              </div>
              <div className="flex-1 space-y-1">
                <label className="text-[10px] text-muted-foreground">Actual ($)</label>
                <Input type="number" className="h-8 text-sm" value={actualInput} onChange={e => setActualInput(e.target.value)} />
              </div>
              <Button size="sm" className="h-8" onClick={saveGoal}><Check className="w-3 h-3 mr-1" /> Save</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => setEditingGoal(false)}>Cancel</Button>
            </div>
          ) : goalTarget > 0 ? (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <div>
                  <span className={`text-lg font-medium tabular-nums ${goalPct >= 100 ? "text-primary" : "text-foreground"}`}>
                    ${goalActual.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">/ ${goalTarget.toLocaleString()}</span>
                </div>
                <span className={`text-sm font-medium ${goalPct >= 100 ? "text-primary" : goalPct >= 60 ? "text-accent" : "text-muted-foreground"}`}>
                  {goalPct}%
                </span>
              </div>
              <Progress value={goalPct} className="h-2" />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Set a weekly revenue target to measure how you're tracking against your goals.</p>
          )}
        </CardContent>
      </Card>

      {/* Cash Flow Trend */}
      {chartData.length > 1 && (
        <Card className="bg-card border-border/60">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium flex items-center gap-2 uppercase tracking-wider text-muted-foreground">
              <Activity className="w-3.5 h-3.5 text-primary" /> Cash Flow Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, "Cash Flow"]}
                  />
                  <Line type="monotone" dataKey="cashFlow" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* P&L Snapshot */}
      {metrics && (
        <Card className="bg-card border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">P&L Snapshot</span>
            </div>
            <div className="space-y-2">
              {[
                { label: "Revenue", value: `$${metrics.revenue.toLocaleString()}`, pct: "100%", color: "text-foreground" },
                { label: "Food Cost", value: `${metrics.foodPct.toFixed(1)}%`, pct: `Avg ${MIAMI_AVG.foodCostPct}%`, color: metrics.foodPct > MIAMI_AVG.foodCostPct + 2 ? "text-destructive" : "text-foreground" },
                { label: "Labor Cost", value: `${metrics.laborPct.toFixed(1)}%`, pct: `Avg ${MIAMI_AVG.laborCostPct}%`, color: metrics.laborPct > MIAMI_AVG.laborCostPct + 2 ? "text-destructive" : "text-foreground" },
                { label: "Rent", value: `${metrics.rentPct.toFixed(1)}%`, pct: `Avg ${MIAMI_AVG.rentPct}%`, color: "text-foreground" },
              ].map((row, i) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{row.label}</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-medium tabular-nums ${row.color}`}>{row.value}</span>
                    <span className="text-[10px] text-muted-foreground w-16 text-right">{row.pct}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────
export default function PostOpeningDashboard() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const locId = activeLocation?.id || null;

  const [activeNav, setActiveNav] = useState<NavSection>("overview");
  const [checkins, setCheckins] = useState<any[]>([]);
  const [permits, setPermits] = useState<any[]>([]);
  const [inspectionChecked, setInspectionChecked] = useState(0);
  const [loading, setLoading] = useState(true);

  const currentWeekLabel = format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-'W'II");
  const [goalTarget, setGoalTarget] = useState(0);
  const [goalActual, setGoalActual] = useState(0);
  const [goalId, setGoalId] = useState<string | null>(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [actualInput, setActualInput] = useState("");

  useEffect(() => {
    if (!user) return;
    let checkinsQ = supabase.from("monthly_checkins").select("*").eq("user_id", user.id);
    let permitsQ = supabase.from("permit_renewals").select("*").eq("user_id", user.id);
    let inspQ = supabase.from("health_inspection_checks").select("item_key, checked").eq("user_id", user.id);
    let goalQ = supabase.from("weekly_revenue_goals").select("*").eq("user_id", user.id).eq("week_label", currentWeekLabel);

    if (locId) {
      checkinsQ = checkinsQ.eq("location_id", locId);
      permitsQ = permitsQ.eq("location_id", locId);
      inspQ = inspQ.eq("location_id", locId);
      goalQ = goalQ.eq("location_id", locId);
    }

    Promise.all([
      checkinsQ.order("created_at", { ascending: false }).limit(6),
      permitsQ.order("expiration_date", { ascending: true }),
      inspQ,
      goalQ.maybeSingle(),
    ]).then(([checkinsRes, permitsRes, inspRes, goalRes]) => {
      setCheckins((checkinsRes.data as any[]) || []);
      setPermits((permitsRes.data as any[]) || []);
      const checked = ((inspRes.data as any[]) || []).filter((d: any) => d.checked).length;
      setInspectionChecked(checked);
      if (goalRes.data) {
        setGoalTarget(goalRes.data.target_revenue);
        setGoalActual(goalRes.data.actual_revenue);
        setGoalId(goalRes.data.id);
      } else {
        setGoalTarget(0); setGoalActual(0); setGoalId(null);
      }
      setLoading(false);
    });
  }, [user, locId, currentWeekLabel]);

  const latest = checkins[0] || null;
  const metrics = useMemo(() => {
    if (!latest) return null;
    const rev = latest.revenue || 1;
    return {
      foodPct: (latest.food_cost / rev) * 100,
      laborPct: (latest.labor_cost / rev) * 100,
      rentPct: (latest.rent / rev) * 100,
      cashFlow: latest.revenue - latest.food_cost - latest.labor_cost - latest.rent,
      revenue: latest.revenue,
    };
  }, [latest]);

  const nextPermit = permits[0] || null;
  const permitDays = nextPermit ? differenceInDays(parseISO(nextPermit.expiration_date), new Date()) : null;
  const inspPct = Math.round((inspectionChecked / VIOLATIONS_COUNT) * 100);

  const healthScore = useMemo(() => {
    let score = 50;
    if (metrics) {
      let finScore = 40;
      if (metrics.foodPct > MIAMI_AVG.foodCostPct + 5) finScore -= 10;
      else if (metrics.foodPct > MIAMI_AVG.foodCostPct) finScore -= 5;
      if (metrics.laborPct > MIAMI_AVG.laborCostPct + 5) finScore -= 10;
      else if (metrics.laborPct > MIAMI_AVG.laborCostPct) finScore -= 5;
      if (metrics.cashFlow < 0) finScore -= 15;
      score = finScore;
    } else { score = 20; }
    if (permitDays === null) score += 15;
    else if (permitDays < 0) score += 0;
    else if (permitDays < 30) score += 10;
    else if (permitDays <= 90) score += 20;
    else score += 30;
    score += Math.round((inspPct / 100) * 30);
    return Math.max(0, Math.min(100, score));
  }, [metrics, permitDays, inspPct]);

  const leaseEnd = typeof window !== "undefined" ? localStorage.getItem("oplif_lease_end") : null;
  const leaseDays = leaseEnd ? differenceInDays(parseISO(leaseEnd), new Date()) : null;

  const alerts = useMemo(() => {
    const a: { text: string; type: "warning" | "danger" | "info"; section?: NavSection; time?: string }[] = [];
    if (metrics) {
      if (metrics.foodPct > 32) a.push({ text: `Food cost at ${metrics.foodPct.toFixed(1)}% — exceeds 32%`, type: "danger", section: "fb", time: "Now" });
      if (metrics.laborPct > 35) a.push({ text: `Labor at ${metrics.laborPct.toFixed(1)}% — exceeds 35%`, type: "danger", section: "labor", time: "Now" });
      if (metrics.cashFlow < 0) a.push({ text: `Negative cash flow: -$${Math.abs(metrics.cashFlow).toLocaleString()}`, type: "danger", section: "revenue", time: "This month" });
    }
    permits.forEach(p => {
      const days = differenceInDays(parseISO(p.expiration_date), new Date());
      if (days <= 30) {
        a.push({
          text: days < 0 ? `"${p.permit_name}" is ${Math.abs(days)}d overdue` : `"${p.permit_name}" expires in ${days}d`,
          type: days < 0 ? "danger" : "warning", section: "permits", time: days < 0 ? "Overdue" : `${days}d left`,
        });
      }
    });
    if (leaseDays !== null && leaseDays <= 90) {
      a.push({ text: leaseDays <= 0 ? `Lease expired ${Math.abs(leaseDays)}d ago` : `Lease renewal in ${leaseDays}d`, type: leaseDays <= 30 ? "danger" : "warning", section: "lease", time: `${Math.abs(leaseDays)}d` });
    }
    if (inspPct < 50) a.push({ text: `Inspection readiness at ${inspPct}%`, type: "info", section: "inspection", time: "Action needed" });
    return a;
  }, [metrics, permits, leaseDays, inspPct]);

  const chartData = useMemo(() => {
    return [...checkins].reverse().map(c => ({
      month: c.month_label,
      cashFlow: c.revenue - c.food_cost - c.labor_cost - c.rent,
    }));
  }, [checkins]);

  const goalPct = goalTarget > 0 ? Math.min(100, Math.round((goalActual / goalTarget) * 100)) : 0;
  const weekEnd = format(endOfWeek(new Date(), { weekStartsOn: 1 }), "MMM d");
  const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), "MMM d");

  const saveGoal = async () => {
    if (!user) return;
    const target = parseInt(goalInput) || goalTarget;
    const actual = parseInt(actualInput) || goalActual;
    if (goalId) {
      await supabase.from("weekly_revenue_goals").update({ target_revenue: target, actual_revenue: actual }).eq("id", goalId);
    } else {
      const { data } = await supabase.from("weekly_revenue_goals").insert({
        user_id: user.id, week_label: currentWeekLabel, target_revenue: target, actual_revenue: actual,
        ...(locId ? { location_id: locId } : {}),
      }).select().single();
      if (data) setGoalId(data.id);
    }
    setGoalTarget(target);
    setGoalActual(actual);
    setEditingGoal(false);
    toast.success("Revenue goal saved");
  };

  if (loading) {
    return <OperationsDashboardSkeleton />;
  }

  // Group nav items
  const groups = NAV_ITEMS.reduce<Record<string, typeof NAV_ITEMS>>((acc, item) => {
    (acc[item.group] = acc[item.group] || []).push(item);
    return acc;
  }, {});

  const renderMainContent = () => {
    switch (activeNav) {
      case "overview":
        return (
          <OverviewContent
            metrics={metrics} healthScore={healthScore} inspPct={inspPct}
            permitDays={permitDays} nextPermit={nextPermit} chartData={chartData}
            goalTarget={goalTarget} goalActual={goalActual} goalPct={goalPct}
            weekStart={weekStart} weekEnd={weekEnd}
            editingGoal={editingGoal} setEditingGoal={setEditingGoal}
            goalInput={goalInput} setGoalInput={setGoalInput}
            actualInput={actualInput} setActualInput={setActualInput}
            saveGoal={saveGoal}
          />
        );
      case "revenue":
        return (
          <div className="space-y-5">
            <SectionHeader title="Revenue & P&L" subtitle="Gross Operating Profit and Department Breakdown" />
            <GOPCalculator />
            <Separator />
            <DepartmentPLBreakdown />
          </div>
        );
      case "fb":
        return (
          <div className="space-y-5">
            <SectionHeader title="F&B Performance" subtitle="Food & Beverage cost variance and RevPAR analysis" />
            <FBCostVarianceReport />
            <Separator />
            <RevPARTracker />
          </div>
        );
      case "labor":
        return (
          <div className="space-y-5">
            <SectionHeader title="Labor Productivity" subtitle="Revenue per labor hour and workforce efficiency" />
            <LaborProductivityIndex />
          </div>
        );
      case "inventory":
        return (
          <div className="space-y-5">
            <SectionHeader title="Purchasing & Stocktake" subtitle="Catalog items, count stock, and auto-generate purchase orders" />
            <InventoryLauncher />
          </div>
        );
      case "staff-rota":
        return (
          <div className="space-y-5">
            <SectionHeader title="Staff Rota" subtitle="Weekly staff schedule with labor cost per shift" />
            <StaffRotaSection />
          </div>
        );
      case "permits":
        return (
          <div className="space-y-5">
            <SectionHeader title="Permit Tracker" subtitle="Track permit renewals and compliance deadlines" />
            <PermitRenewalTracker />
          </div>
        );
      case "inspection":
        return (
          <div className="space-y-5">
            <SectionHeader title="Health Inspection Prep" subtitle="Ensure inspection readiness with the full checklist" />
            <HealthInspectionPrep />
          </div>
        );
      case "lease":
        return (
          <div className="space-y-5">
            <SectionHeader title="Lease Tracker" subtitle="Monitor lease terms and renewal timelines" />
            <LeaseTracker />
          </div>
        );
      case "benchmarks":
        return (
          <div className="space-y-5">
            <SectionHeader title="KPI Benchmarking" subtitle="Compare performance against STR/NRA industry data" />
            <KPIBenchmarkingTool />
          </div>
        );
      case "daily-report":
        return (
          <div className="space-y-5">
            <SectionHeader title="Manager Daily Report" subtitle="Auto-generated executive summary for today" />
            <ManagerDailyReport />
          </div>
        );
      case "checkin":
        return (
          <div className="space-y-5">
            <SectionHeader title="Monthly Check-In" subtitle="Log monthly financials to track trends" />
            <MonthlyCheckIn />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-0 min-h-[calc(100vh-10rem)] -mx-4 sm:-mx-6 lg:-mx-8">
      {/* ─── LEFT: Navigation Sidebar ─── */}
      <aside className="w-56 shrink-0 border-r border-border bg-muted/30 hidden lg:block">
        <ScrollArea className="h-full">
          <div className="p-4">
            {/* Health Score */}
            <div className="mb-4 pb-4 border-b border-border">
              <MiniGauge score={healthScore} />
            </div>

            {/* Nav Groups */}
            {Object.entries(groups).map(([group, items]) => (
              <div key={group} className="mb-4">
                <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60 px-2">
                  {group}
                </span>
                <nav className="mt-1.5 space-y-0.5">
                  {items.map(item => {
                    const isActive = activeNav === item.id;
                    const hasAlert = alerts.some(a => a.section === item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveNav(item.id)}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all duration-200 ease-out ${
                          isActive
                            ? "bg-card text-foreground font-medium shadow-sm border border-border/60"
                            : "text-muted-foreground hover:text-foreground hover:bg-card/50"
                        }`}
                      >
                        <item.icon className={`w-4 h-4 shrink-0 transition-colors duration-200 ${isActive ? "text-primary" : ""}`} />
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        {hasAlert && (
                          <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0 animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        </ScrollArea>
      </aside>

      {/* ─── Mobile Nav ─── */}
      <div className="lg:hidden sticky top-0 z-40 bg-card border-b border-border px-2 py-2 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {NAV_ITEMS.map(item => {
            const isActive = activeNav === item.id;
            const hasAlert = alerts.some(a => a.section === item.id);
            return (
              <button
                key={item.id}
                onClick={() => setActiveNav(item.id)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium shrink-0 transition-all duration-200 ease-out ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <item.icon className="w-3.5 h-3.5" />
                <span>{item.label}</span>
                {hasAlert && <span className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── MIDDLE: Main Content ─── */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-5 lg:p-6 pb-20 lg:pb-6">
          <motion.div
            key={activeNav}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {renderMainContent()}
          </motion.div>
        </div>
      </main>

      {/* ─── RIGHT: Alerts & Activity ─── */}
      <aside className="w-72 shrink-0 border-l border-border bg-muted/20 hidden xl:block">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-5">
            {/* Smart Alerts */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-muted-foreground" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Smart Alerts
                </span>
                {alerts.length > 0 && (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                    {alerts.length}
                  </Badge>
                )}
              </div>
              {alerts.length === 0 ? (
                <div className="text-center py-6">
                  <Check className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Everything looks good — no action items right now.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.map((alert, i) => (
                    <button
                      key={i}
                      onClick={() => alert.section && setActiveNav(alert.section)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors hover:bg-card/80 ${
                        alert.type === "danger"
                          ? "border-destructive/30 bg-destructive/5"
                          : alert.type === "warning"
                          ? "border-accent/30 bg-accent/5"
                          : "border-border bg-card/50"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                          alert.type === "danger" ? "text-destructive" : alert.type === "warning" ? "text-accent" : "text-muted-foreground"
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground leading-relaxed">{alert.text}</p>
                          <span className="text-[10px] text-muted-foreground mt-1 block">{alert.time}</span>
                        </div>
                        <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Quick Actions */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-muted-foreground" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Quick Actions
                </span>
              </div>
              <div className="space-y-1.5">
                {[
                  { label: "Log Monthly Check-In", section: "checkin" as NavSection, icon: Activity },
                  { label: "Add Permit", section: "permits" as NavSection, icon: Shield },
                  { label: "Run Benchmarks", section: "benchmarks" as NavSection, icon: Trophy },
                  { label: "View Daily Report", section: "daily-report" as NavSection, icon: FileText },
                ].map(action => (
                  <button
                    key={action.label}
                    onClick={() => setActiveNav(action.section)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-card/60 transition-colors"
                  >
                    <action.icon className="w-3.5 h-3.5" />
                    <span>{action.label}</span>
                    <ChevronRight className="w-3 h-3 ml-auto" />
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Permit Status Mini */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Permit Status
                </span>
              </div>
              {permitDays !== null ? (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border/60">
                  <div className={`text-lg font-medium tabular-nums ${
                    permitDays < 0 ? "text-destructive" : permitDays < 30 ? "text-destructive" : permitDays <= 90 ? "text-accent" : "text-primary"
                  }`}>
                    {permitDays < 0 ? `${Math.abs(permitDays)}d` : `${permitDays}d`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{nextPermit?.permit_name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {permitDays < 0 ? "Overdue" : "Until renewal"}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Add permits to track deadlines</p>
              )}
            </div>

            <Separator />

            {/* Inspection Readiness */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-4 h-4 text-muted-foreground" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Inspection Ready
                </span>
              </div>
              <div className="p-3 rounded-lg bg-card/50 border border-border/60">
                <div className="flex items-baseline justify-between mb-2">
                  <span className={`text-lg font-medium ${inspPct >= 80 ? "text-primary" : inspPct >= 50 ? "text-accent" : "text-destructive"}`}>
                    {inspPct}%
                  </span>
                  <span className="text-[10px] text-muted-foreground">{inspectionChecked}/{VIOLATIONS_COUNT} items</span>
                </div>
                <Progress value={inspPct} className="h-1.5" />
              </div>
            </div>
          </div>
        </ScrollArea>
      </aside>
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-1">
      <h2 className="text-lg font-heading font-medium text-foreground">{title}</h2>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
  );
}
